import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../supabaseClient';
import { useBusinessType } from '../../context/BusinessContext';
import { Plus, Trash, Funnel, BookOpen, X, CircleNotch, PencilSimple, Wrench, Database, GitBranch } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
import { ConfirmModal } from '../ui/confirm-modal';

const ClarificationQuestionEditor = ({ question, onSave, onDelete, isMobile }) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);

  const [formData, setFormData] = useState(question);
  const [newAnswer, setNewAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [originalFormData, setOriginalFormData] = useState(question);
  const [connectedSettings, setConnectedSettings] = useState([]);
  const [groupedConnectedSettings, setGroupedConnectedSettings] = useState({});
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const inputRef = useRef(null);
  const editInputRef = useRef(null);
  const nameInputRef = useRef(null);

  const hasUnsavedChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);

  // Fetch connected settings for existing qualifiers
  const fetchConnectedSettings = async (topicId) => {
    if (!topicId || formData?.isNew) {
      setConnectedSettings([]);
      setGroupedConnectedSettings({});
      return;
    }

    setIsLoadingConnections(true);
    try {
      const { data, error } = await supabase
        .from('brain_setting_requirements')
        .select(`
          id,
          required_answer,
          brain_settings!inner (
            id,
            name,
            type,
            description,
            brain_categories!inner (
              name
            )
          )
        `)
        .eq('topic_id', topicId);

      if (error) throw error;

      // Group settings by their ID
      const grouped = (data || []).reduce((acc, conn) => {
        const settingId = conn.brain_settings.id;
        if (!acc[settingId]) {
          acc[settingId] = {
            setting: conn.brain_settings,
            connections: []
          };
        }
        acc[settingId].connections.push({
          id: conn.id,
          required_answer: conn.required_answer
        });
        return acc;
      }, {});

      setGroupedConnectedSettings(grouped);
      setConnectedSettings(data || []); // Keep original for reference if needed
    } catch (error) {
      console.error('Error fetching connected settings:', error);
      setConnectedSettings([]);
      setGroupedConnectedSettings({});
    } finally {
      setIsLoadingConnections(false);
    }
  };

  useEffect(() => {
    setFormData(question);
    setOriginalFormData(question);

    // Focus on name input for new questions, otherwise focus on add answer input
    const timer = setTimeout(() => {
      if (question?.isNew) {
        nameInputRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }, 100);

    // Fetch connected settings for existing qualifiers
    if (question && !question.isNew) {
      fetchConnectedSettings(question.id);
    }

    return () => clearTimeout(timer);
  }, [question]);

  // Focus on edit input when edit mode is enabled
  useEffect(() => {
    if (editIndex !== null) {
      const timer = setTimeout(() => {
        editInputRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [editIndex]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddAnswer = () => {
    if (!newAnswer.trim()) return;
    if (formData.possible_answers.includes(newAnswer.trim())) {
      toast("This intent is already in the list", {
        style: { color: "#ef4444", borderColor: "#fecaca", backgroundColor: "#fef2f2" }
      });
      return;
    }
    setFormData(prev => ({
      ...prev,
      possible_answers: [...prev.possible_answers, newAnswer.trim()]
    }));
    setNewAnswer('');
    inputRef.current?.focus();
  };

  const handleRemoveAnswer = (index) => {
    const updatedAnswers = [...formData.possible_answers];
    updatedAnswers.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      possible_answers: updatedAnswers
    }));
  };

  const handleEditAnswer = (index) => {
    setEditIndex(index);
    setNewAnswer(formData.possible_answers[index]);
  };

  const handleSaveEdit = () => {
    if (!newAnswer.trim()) {
      toast("Intent cannot be empty", {
        style: { color: "#ef4444", borderColor: "#fecaca", backgroundColor: "#fef2f2" }
      });
      return;
    }

    // Don't add duplicates (except the current one being edited)
    const otherAnswers = formData.possible_answers.filter((_, i) => i !== editIndex);
    if (otherAnswers.includes(newAnswer.trim())) {
      toast("This intent is already in the list", {
        style: { color: "#ef4444", borderColor: "#fecaca", backgroundColor: "#fef2f2" }
      });
      return;
    }

    const updatedAnswers = [...formData.possible_answers];
    updatedAnswers[editIndex] = newAnswer.trim();
    setFormData(prev => ({
      ...prev,
      possible_answers: updatedAnswers
    }));
    setNewAnswer('');
    setEditIndex(null);
    inputRef.current?.focus();
  };

  const handleCancelEdit = () => {
    setNewAnswer('');
    setEditIndex(null);
    inputRef.current?.focus();
  };

  // Get type configuration for brain settings (matching BrainSidebar)
  const getTypeConfig = (type, action_type = null) => {
    const baseTypes = {
      'simple_answer': {
        icon: BookOpen, 
        color: 'bg-blue-100 text-blue-700',
        title: 'Simple Answer'
      },
      'complex_answer': { 
        icon: Database, 
        color: 'bg-purple-100 text-purple-700',
        title: 'Complex Answer'
      },
      'simple_action': {
        icon: Wrench, 
        color: 'bg-amber-100 text-amber-700',
        title: 'Simple Action'
      },
      'complex_action': { 
        icon: GitBranch, 
        color: 'bg-green-100 text-green-700',
        title: 'Complex Action'
      },
      'workflow': { 
        icon: GitBranch, 
        color: 'bg-green-100 text-green-700',
        title: 'Workflow'
      },
      'default': { 
        icon: BookOpen,
        color: 'bg-gray-100 text-gray-700',
        title: 'Unknown Type'
      }
    };

    return baseTypes[type] || baseTypes.default;
  };

  const handleSave = async () => {
    if (!formData.topic_name?.trim()) {
      toast.error('Qualifier Name is required.');
      return;
    }
    if (!formData.question_to_ask?.trim()) {
      toast.error('Question is required.');
      return;
    }
    if (formData.possible_answers.length === 0) {
              toast.error('At least one possible intent is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (formData.isNew) {
        // Create new - get account_id if not provided
        let accountId = formData.account_id;
        if (!accountId) {
          const { getUserAccountId } = await import('../../utils/accountUtils');
          const { accountId: userAccountId, error: accountIdError } = await getUserAccountId();
          if (accountIdError || !userAccountId) {
            throw new Error('Failed to get account ID');
          }
          accountId = userAccountId;
        }

        // Generate temporary ID for optimistic update
        const tempId = `temp_${Date.now()}`;
        const optimisticData = {
          id: tempId,
          account_id: accountId,
          topic_name: formData.topic_name.trim(),
          question_to_ask: formData.question_to_ask.trim(),
          possible_answers: formData.possible_answers,
          created_at: new Date().toISOString(),
          display_order: 0
        };

        // Optimistic update - show in UI immediately
        onSave(optimisticData);
        toast.success('Qualifier created successfully');

        const insertData = {
          account_id: accountId,
          topic_name: formData.topic_name.trim(),
          question_to_ask: formData.question_to_ask.trim(),
          possible_answers: formData.possible_answers,
        };

        const { data, error } = await supabase
          .from('brain_conversation_topics')
          .insert(insertData)
          .select()
          .single();

        if (error) {
          console.error('Supabase error:', error);
          // Revert optimistic update on error
          onSave({ ...optimisticData, id: tempId, _shouldRemove: true });
          throw error;
        }

        // Update with real data from database
        onSave({ ...data, _replaceTemp: tempId });
      } else {
        // Update existing
        const updateData = {
          topic_name: formData.topic_name.trim(),
          question_to_ask: formData.question_to_ask.trim(),
          possible_answers: formData.possible_answers,
        };

        console.log('Updating data:', updateData);

        const { data, error } = await supabase
          .from('brain_conversation_topics')
          .update(updateData)
          .eq('id', formData.id)
          .select()
          .single();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }
        toast.success('Qualifier updated successfully');
        onSave(data);
        setOriginalFormData(data);

        // Refresh connected settings after update
        fetchConnectedSettings(data.id);
      }
    } catch (err) {
      console.error('Error saving clarification question:', err);
      if (err.message) {
        toast.error(`Failed to save: ${err.message}`);
      } else {
        toast.error('Failed to save. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('brain_conversation_topics')
        .delete()
        .eq('id', formData.id);

      if (error) throw error;
      toast.success('Qualifier deleted successfully');
      setIsDeleteConfirmOpen(false);
      onDelete(formData.id);
    } catch (err) {
      console.error('Error deleting clarification question:', err);
      toast.error('Failed to delete. It might be in use by a brain setting.');
      setIsDeleteConfirmOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants (same as RoadsPopup)
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ 
      opacity: 1, 
      y: 0,
      transition: { 
        delay: i * 0.05,
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    }),
    exit: { 
      opacity: 0,
      y: -10,
      transition: { duration: 0.2 }
    },
    removed: {
      opacity: 0,
      x: -50,
      transition: { duration: 0.2 }
    }
  };

  if (!formData) {
    return (
      <div className="flex-1 bg-white h-full overflow-hidden flex items-center justify-center">
        <div className="text-center text-gray-500">Select an item from the sidebar to begin.</div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-white h-full flex flex-col overflow-hidden">
      {/* Header - matching RoadsPopup style */}
      <motion.div 
        className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b sticky top-0 bg-white z-10"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="p-1.5 sm:p-2 bg-gray-100/80 rounded-lg">
            <Funnel size={16} className="text-gray-700 sm:w-[18px] sm:h-[18px]" />
          </div>
          <div>
            <h2 className="font-medium text-base sm:text-lg text-gray-900">
              {formData.isNew ? 'Create New Qualifier' : 'Edit Qualifier'}
            </h2>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">
              Qualify user intent for answers and actions.
            </p>
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleSave}
          disabled={isSubmitting || editIndex !== null || !hasUnsavedChanges}
          className="py-2 sm:py-2.5 px-4 sm:px-5 rounded-xl bg-black text-white text-xs sm:text-sm font-medium
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="mr-2 h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent rounded-full"
              />
              Saving...
            </div>
          ) : (
            "Save Changes"
          )}
        </motion.button>
      </motion.div>

      {/* Content - matching RoadsPopup scroll area */}
      <div className="p-5 sm:p-7 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
        {/* How it works section */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3 sm:p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 p-1.5 bg-white rounded-lg border border-gray-200">
                <GitBranch size={16} className="text-gray-600 sm:w-[18px] sm:h-[18px]" />
              </div>
              <div className="flex-1">
                <h4 className="text-xs sm:text-sm font-medium text-gray-800 mb-1">
                  How These Qualifiers Work
                </h4>
                <p className="text-[10px] sm:text-xs text-gray-600">
                  These qualifiers help your AI understand user intent. Certain answers and actions should only occur when the user has a specific intent. If the AI doesn't know the intent, it will ask the Question to qualify the user before providing answers or taking actions.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Name Input */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2"
        >
                        <label className="text-xs sm:text-sm font-medium text-gray-700">Qualifier Name</label>
          <input
            ref={nameInputRef}
            type="text"
            name="topic_name"
            value={formData.topic_name || ''}
            onChange={handleChange}
            placeholder="e.g., Service Type"
            disabled={isSubmitting}
            className="w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm 
              focus:outline-none focus:ring-2 focus:ring-black/50 focus:border-transparent
              placeholder:text-gray-400 disabled:opacity-50"
          />
        </motion.div>

        {/* Question Input */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="space-y-2"
        >
                        <label className="text-xs sm:text-sm font-medium text-gray-700">Question To Decide What To Do</label>
          <textarea
            name="question_to_ask"
            value={formData.question_to_ask || ''}
            onChange={handleChange}
            placeholder="e.g., What type of service are you interested in?"
            disabled={isSubmitting}
            rows={3}
            className="w-full border border-gray-200 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm 
              focus:outline-none focus:ring-2 focus:ring-black/50 focus:border-transparent
              placeholder:text-gray-400 disabled:opacity-50 resize-none"
          />
        </motion.div>

        {/* Add Answer Input - matching RoadsPopup style */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-2"
        >
          <label className="text-xs sm:text-sm font-medium text-gray-700">Possible Intent</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                ref={editIndex === null ? inputRef : editInputRef}
                type="text"
                value={newAnswer}
                onChange={(e) => setNewAnswer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (editIndex !== null) {
                      handleSaveEdit();
                    } else {
                      handleAddAnswer();
                    }
                  } else if (e.key === 'Escape' && editIndex !== null) {
                    handleCancelEdit();
                  }
                }}
                placeholder={editIndex !== null 
                  ? "Edit answer" 
                  : "e.g., Wash & Fold"
                }
                className="w-full border border-gray-200 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm 
                  focus:outline-none focus:ring-2 focus:ring-black/50 focus:border-transparent
                  placeholder:text-gray-400"
                disabled={isSubmitting}
              />
            </div>

            {editIndex !== null ? (
              <div className="flex space-x-1 sm:space-x-2">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSaveEdit}
                  disabled={!newAnswer.trim() || isSubmitting}
                  className="h-10 sm:h-11 px-3 sm:px-4 rounded-xl bg-black text-white text-xs sm:text-sm font-medium
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Update
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleCancelEdit}
                  disabled={isSubmitting}
                  className="h-10 sm:h-11 px-3 sm:px-4 rounded-xl border border-gray-200 text-gray-700 text-xs sm:text-sm
                    hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </motion.button>
              </div>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddAnswer}
                disabled={!newAnswer.trim() || isSubmitting}
                className="h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center rounded-xl bg-black text-white
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={16} className="sm:w-[18px] sm:h-[18px]" />
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Intent list - exactly like RoadsPopup */}
        <div className="space-y-1.5 sm:space-y-2">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="flex items-center justify-between"
          >
            <h3 className="text-xs sm:text-sm font-medium text-gray-700">
              {formData.possible_answers.length} Intent{formData.possible_answers.length !== 1 ? 's' : ''}
            </h3>
          </motion.div>

          <AnimatePresence>
            {formData.possible_answers.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-10 sm:py-12 px-4 
                  rounded-xl border border-dashed border-gray-200 text-center"
              >
                <Funnel size={20} className="text-gray-300 mb-2 sm:mb-3 sm:w-6 sm:h-6" />
                <p className="text-gray-500 text-xs sm:text-sm">
                  No intent added yet.
                </p>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                  Add possible intents customers might have.
                </p>
              </motion.div>
            ) : (
              <motion.ul 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="rounded-xl overflow-hidden border border-gray-100 divide-y divide-gray-100"
              >
                <AnimatePresence>
                  {formData.possible_answers.map((answer, index) => (
                    <motion.li 
                      key={`answer-${answer}-${index}`}
                      custom={index}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="removed"
                      className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white
                        hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-gray-800 text-xs sm:text-sm font-light mr-2">{answer}</span>
                      <div className="flex items-center space-x-1">
                        <motion.button
                          whileHover={{ scale: 1.1, backgroundColor: '#f0f9ff' }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEditAnswer(index)}
                          className="text-gray-500 hover:text-blue-500 rounded-full w-7 h-7 sm:w-8 sm:h-8 
                            flex items-center justify-center hover:bg-blue-50"
                          disabled={isSubmitting || editIndex !== null}
                          aria-label={`Edit ${answer}`}
                        >
                          <PencilSimple size={14} className="sm:w-[15px] sm:h-[15px]" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1, backgroundColor: '#fef2f2' }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleRemoveAnswer(index)}
                          className="text-gray-500 hover:text-red-500 rounded-full w-7 h-7 sm:w-8 sm:h-8 
                            flex items-center justify-center hover:bg-red-50"
                          disabled={isSubmitting || editIndex !== null}
                          aria-label={`Remove ${answer}`}
                        >
                          <Trash size={14} className="sm:w-[15px] sm:h-[15px]" />
                        </motion.button>
                      </div>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </motion.ul>
            )}
          </AnimatePresence>
        </div>

        {/* Connected Settings Section - matching RoadsPopup style */}
        {!formData.isNew && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-1.5 sm:space-y-2 pt-4 sm:pt-5 border-t border-gray-100"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700">
                Connected Settings
              </h3>
              {isLoadingConnections && (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="h-3 w-3 sm:h-4 sm:w-4 border-2 border-gray-300 border-t-transparent rounded-full"
                />
              )}
            </div>

            <AnimatePresence>
              {Object.keys(groupedConnectedSettings).length === 0 && !isLoadingConnections ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-6 sm:py-8 px-4 
                    rounded-xl border border-dashed border-gray-200 text-center"
                >
                  <Funnel size={16} className="text-gray-300 mb-2 sm:w-5 sm:h-5" />
                  <p className="text-gray-500 text-xs sm:text-sm">
                    No settings use this qualifier yet.
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                                          Add conditions to brain settings to use these intents.
                  </p>
                </motion.div>
              ) : (
                <motion.ul 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="rounded-xl overflow-hidden border border-gray-100 divide-y divide-gray-100"
                >
                  <AnimatePresence>
                    {Object.values(groupedConnectedSettings).map(({ setting, connections }, index) => {
                      const typeConfig = getTypeConfig(setting.type);
                      const IconComponent = typeConfig.icon;

                      return (
                        <motion.li 
                          key={`connection-group-${setting.id}`}
                          custom={index}
                          variants={itemVariants}
                          initial="hidden"
                          animate="visible"
                          exit="removed"
                          className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className={cn("w-5 h-5 rounded flex items-center justify-center flex-shrink-0", typeConfig.color)}>
                              <IconComponent className="w-3 h-3" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-800 text-xs sm:text-sm font-light truncate">{setting.name}</span>
                                <span className="text-gray-400 text-xs">•</span>
                                <span className="text-gray-500 text-xs truncate">{setting.brain_categories.name}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            {connections.map((conn, index) => (
                              <React.Fragment key={index}>
                                <span className="text-[10px] text-blue-600 bg-blue-50 rounded px-1.5 py-0.5 font-medium">
                                  {conn.required_answer}
                                </span>
                                {index < connections.length - 1 && (
                                  <span className="text-gray-300 text-[10px]">or</span>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </motion.li>
                      );
                    })}
                  </AnimatePresence>
                </motion.ul>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Delete Section - Only for existing qualifiers */}
        {!formData.isNew && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-1.5 sm:space-y-2 pt-4 sm:pt-5 border-t border-gray-100"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700">
                Danger Zone
              </h3>
            </div>

            <div className="rounded-xl border border-red-200 bg-red-50/30 p-3 sm:p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-1">
                  <h4 className="text-xs sm:text-sm font-medium text-red-800 mb-1">
                    Delete Qualifier
                  </h4>
                  <p className="text-[10px] sm:text-xs text-red-600 mb-3">
                    This will permanently delete this qualifier and all its connections to brain settings.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    disabled={isSubmitting || editIndex !== null}
                    className="py-2 sm:py-2.5 px-3 sm:px-4 rounded-xl border border-red-300 text-red-700 text-xs sm:text-sm font-medium
                      bg-white hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="mr-2 h-3 w-3 sm:h-4 sm:w-4 border-2 border-red-700 border-t-transparent rounded-full"
                        />
                        Deleting...
                      </div>
                                          ) : (
                        "Delete Qualifier"
                      )}
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Qualifier"
        message="Are you sure you want to delete this qualifier? This action cannot be undone."
                 confirmText="Delete Qualifier"
         confirmStyle="bg-red-600 hover:bg-red-700"
         cancelText="Cancel"
      />
    </div>
  );
};

export default ClarificationQuestionEditor; 